import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { LOGIN_SUCCESS } from '../../store/action-types';
import { fetchVideoPageData } from '../../store/media/videos/actions';
import { getMessage } from '../../store/activity-stream/selectors';
import {
  isLoadingVideo,
  getVideo,
  getRelatedVideos,
  hasLoadedVideo,
  getVideoPageLoadingError,
  getNextVideo,
} from '../../store/media/videos/selectors';
import Club404 from '../../components/errors/club-404';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import SeoHead from '../../components/seo';
import Video from '../../components/media/video';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import { withTranslation } from '../../lib/i18n';

class VideoPage extends React.Component {
  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { videoId } = this.props;
    const { videoId: previousVideoId } = prevProps;
    if (videoId !== previousVideoId) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const {
      isLoading,
      previouslyLoaded,
      club: { galleryEnabled },
    } = this.props;
    if (galleryEnabled && !isLoading && !previouslyLoaded) {
      const { clubId, loadVideo, videoId } = this.props;
      loadVideo(clubId, videoId);
    }
  };

  render() {
    const { club, loadingError } = this.props;
    if (!club.galleryEnabled) {
      return <Club404 club={club} />;
    }

    if (loadingError) {
      return (
        <PageDataLoadError
          clubId={club.id}
          accessDeniedTranslationKey="media:video_not_authorised"
          onAuthorise={this.loadIfRequired}
          {...loadingError}
        />
      );
    }

    const { message, nextVideo, video, videoId, related, isLoading, t } = this.props;
    let title = t('common:loading_title');
    const videoProps = {
      club,
      isLoading,
      title,
      videoId,
      nextVideo,
    };

    if (video) {
      ({ title } = video);
      ({
        author: videoProps.author,
        description: videoProps.description,
        duration: videoProps.duration,
        height: videoProps.videoHeight,
        published: videoProps.published,
        team_id: videoProps.teamId,
        thumbnail: videoProps.thumbnail,
        title: videoProps.title,
        url: videoProps.videoUrl,
        width: videoProps.videoWidth,
        views: videoProps.videoViews,
      } = video);
    } else if (message && !message.isPrivate) {
      ({ title } = message);
      // If we haven't loaded the video yet we may have the data available from the activity message.
      ({
        author: videoProps.author,
        description: videoProps.description,
        duration: videoProps.duration,
        published: videoProps.published,
        team_id: videoProps.teamId,
        thumbnail: videoProps.thumbnail,
        title: videoProps.title,
        url: videoProps.videoUrl,
      } = message);
    }
    return (
      <>
        <SeoHead
          descriptionTranslationKey="media:video_meta_description"
          title={title}
          route="media.video"
          routeParams={{
            _title: title,
            id: videoId,
          }}
          translationValues={{
            authorName: get(video, 'author.name'),
          }}
        />
        <Video {...videoProps} related={related} />
      </>
    );
  }
}

VideoPage.getInitialProps = async ({ query, isServer, reduxStore, club, req, res }) => {
  const videoId = parseInt(query.id, 10);
  const { id: clubId } = club;
  const initialProps = {
    activeSection: 'videos',
    clubId,
    videoId,
    namespacesRequired: ['common', 'media'],
    pageType: 'media.video',
  };
  if (!isServer) {
    return initialProps;
  }

  if (!club.galleryEnabled) {
    res.statusCode = 404;
    return initialProps;
  }

  try {
    const { error, accessLevel } = await reduxStore.dispatch(
      fetchVideoPageData(clubId, videoId, req),
    );

    if (error && error.errorStatusCode && res) {
      res.statusCode = error.errorStatusCode;
    }

    if (accessLevel && accessLevel === 'private') {
      res.doNotCache = true;
      if (!error) {
        reduxStore.dispatch({ type: LOGIN_SUCCESS });
      }
    }

    return initialProps;
  } catch (err) {
    return initialProps;
  }
};

VideoPage.defaultProps = {
  loadingError: null,
  message: null,
  nextVideo: null,
  related: [],
  video: null,
};

VideoPage.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  clubId: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadingError: PropTypes.shape({
    message: PropTypes.string,
    statusCode: PropTypes.number,
  }),
  loadVideo: PropTypes.func.isRequired,
  message: PropTypes.shape({
    author: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    duration: PropTypes.number.isRequired,
    isPrivate: PropTypes.bool,
    published: PropTypes.string.isRequired,
    team_id: PropTypes.number,
    thumbnail: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  nextVideo: PropTypes.shape(),
  previouslyLoaded: PropTypes.bool.isRequired,
  related: PropTypes.arrayOf(PropTypes.shape()),
  t: PropTypes.func.isRequired,
  video: PropTypes.shape({
    author: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    duration: PropTypes.number.isRequired,
    height: PropTypes.number,
    published: PropTypes.string.isRequired,
    team_id: PropTypes.number,
    thumbnail: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    width: PropTypes.number,
  }),
  videoId: PropTypes.number.isRequired,
};

const dispatchToProps = {
  loadVideo: fetchVideoPageData,
};

const mapStateToProps = (state, ownProps) => ({
  isLoading: isLoadingVideo(state, ownProps.videoId),
  loadingError: getVideoPageLoadingError(state, ownProps.videoId),
  message: getMessage(state, `video-${ownProps.videoId}`),
  previouslyLoaded: hasLoadedVideo(state, ownProps.videoId),
  related: getRelatedVideos(state, ownProps.videoId),
  nextVideo: getNextVideo(state, ownProps.videoId),
  video: getVideo(state, ownProps.videoId),
});

export default connect(mapStateToProps, dispatchToProps)(withTranslation(['common'])(VideoPage));
